import { AuditOutlined, ReloadOutlined } from '@ant-design/icons';
import type { TableProps } from 'antd';

import { DataTable, PageHeader } from '../../../components/shared';
import { Badge, Button } from '../../../components/ui';
import { useT } from '../../../i18n';
import { KHONG_CO, ngayGioVN, soVN } from '../../../lib/dinh-dang';
import { VungDuLieu } from '../components/VungDuLieu';
import { KET_LUAN } from '../constants';
import { useHangChoPheDuyet } from '../hooks/useThamDinh';
import type { KetLuan, MucHangCho } from '../types';

/**
 * Phê duyệt thẩm định (`/client/tham-dinh/phe-duyet`): hàng chờ ký của chính người đang đăng nhập.
 *
 * Không có bộ lọc: hàng chờ đã là của riêng người gọi (đặc tả mục 10.4), và một danh sách vốn chỉ vài
 * dòng thì thêm bộ lọc chỉ làm người dùng phải quyết định thừa.
 *
 * ⚠️ `GET /phe-duyet/hang-cho` thuộc đợt sau của ADR-0080 mục 9.
 */
export function PheDuyetThamDinhView() {
  const { t, locale } = useT();
  const q = useHangChoPheDuyet();

  const cot: TableProps<MucHangCho>['columns'] = [
    {
      title: t('thamDinh.hangCho.cotTieuDe'),
      dataIndex: 'tieu_de',
      render: (_, r) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-content-primary">{r.tieu_de}</div>
          <div className="truncate text-2xs text-content-muted">{r.bo_ten}</div>
        </div>
      ),
    },
    {
      title: t('thamDinh.hangCho.cotKetLuan'),
      dataIndex: 'ket_luan',
      width: 170,
      render: (v: KetLuan | null) =>
        v ? <Badge variant={KET_LUAN[v].variant}>{t(KET_LUAN[v].khoa)}</Badge> : <span className="text-content-muted">{KHONG_CO}</span>,
    },
    {
      title: t('thamDinh.hangCho.cotDiem'),
      dataIndex: 'diem',
      width: 90,
      align: 'end',
      render: (v: number | null) => (v === null ? KHONG_CO : soVN(v, locale)),
    },
    {
      title: t('thamDinh.hangCho.cotCap'),
      dataIndex: 'cap',
      width: 120,
      render: (v: number) => t('thamDinh.hangCho.capSo', { so: soVN(v, locale) }),
    },
    { title: t('thamDinh.hangCho.cotNguoiTrinh'), dataIndex: 'nguoi_trinh', width: 170 },
    {
      title: t('thamDinh.hangCho.cotTrinhLuc'),
      dataIndex: 'trinh_luc',
      width: 150,
      render: (v: string) => ngayGioVN(v, locale),
    },
  ];

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[{ label: t('nav.nhom.thamDinhAi') }, { label: t('nav.muc.pheDuyet') }]}
          titleIcon={<AuditOutlined className="text-primary-600" />}
          title={t('thamDinh.pheDuyetTieuDe')}
          subtitle={t('thamDinh.pheDuyetMo')}
          actions={
            <Button
              variant="ghost"
              size="md"
              title={t('chung.lamMoi')}
              aria-label={t('chung.lamMoi')}
              onClick={() => void q.refetch()}
            >
              <ReloadOutlined className={q.isFetching ? 'animate-spin' : ''} />
            </Button>
          }
        />

        <div className="rounded-lg border border-default bg-surface shadow-sm">
          <VungDuLieu
            chuaMo
            dangTai={q.isPending}
            loi={q.error}
            dulieu={q.data}
            rong={(q.data?.items.length ?? 0) === 0}
            onThuLai={() => void q.refetch()}
            rongIcon={AuditOutlined}
            rongTieuDe={t('thamDinh.hangCho.rong')}
            rongMo={t('thamDinh.hangCho.rongMo')}
          >
            {(du) => (
              <DataTable<MucHangCho>
                caption={t('thamDinh.hangCho.caption')}
                rowKey="phien_id"
                columns={cot}
                dataSource={du.items}
                pagination={false}
              />
            )}
          </VungDuLieu>
        </div>
      </div>
    </div>
  );
}
